import Skeleton from '../../reusable/Skeleton/Skeleton';
import LineChartSkeleton from '../../reusable/LineChart/LineChartSkeleton';
import { t } from '../../i18n/i18n';
import './Dashboard.scss';

/**
 * Placeholders for the Dashboard's sections while their own request is in
 * flight. The page draws from three endpoints — `/dashboard/` (summary row),
 * `/accounts/` (table) and `/chart/` (curve) — which land at different times,
 * so each section waits on its own rather than the page waiting on all three.
 *
 * Both pieces render the real layout classes — `dash-summary`, `dash-profile`,
 * `dash-stat`, `dash-chart` — so the box is already the size it will be and
 * only the contents are stand-ins. The summary row is 220px and the chart card
 * taller still; a table that paints first and is then shoved down by both is a
 * worse first paint than one that never moves.
 *
 * The accounts table needs nothing here: `DataTable` already draws skeleton
 * rows under its real header when `loading` is set.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** The summary row — `/v2/dashboard/`. */
export function SummarySkeleton() {
  return (
    <section className="dash-summary" aria-busy="true" aria-label={t('Loading account summary')}>
      <div className="dash-profile">
        <Skeleton w="76px" h={76} light pill />

        <div className="dash-profile__foot">
          <div className="dash-profile__id">
            <Skeleton w="132px" h={18} light />
            <Skeleton w="96px" h={10} light />
          </div>
          <div className="dash-profile__actions">
            <Skeleton w="104px" h={32} light />
            <Skeleton w="84px" h={32} light />
          </div>
        </div>
      </div>

      {range(3).map((i) => (
        <div className="dash-stat" key={i}>
          <div className="dash-stat__head">
            <Skeleton w="24px" h={24} />
            <Skeleton w="104px" h={12} />
          </div>
          <div className="dash-stat__body">
            <Skeleton w="min(168px, 90%)" h={34} />
            <Skeleton w="min(132px, 75%)" h={11} />
          </div>
        </div>
      ))}
    </section>
  );
}

/**
 * The curve card — `/v2/dashboard/chart/`.
 *
 * The two tab groups are drawn as bars rather than live `SegmentedTabs`: a
 * range the trader can press before there is a curve to redraw would be
 * claiming a state the card can't honour yet.
 */
export function ChartSkeleton() {
  return (
    <div className="dash-chart" aria-busy="true" aria-label={t('Loading performance curve')}>
      <header className="dash-chart__head">
        <div className="dash-chart__left">
          <Skeleton w="52px" h={13} />
          <Skeleton w="136px" h={32} />
        </div>
        <Skeleton w="204px" h={32} />
      </header>

      <div className="dash-chart__summary">
        <Skeleton w="164px" h={34} />
        <Skeleton w="200px" h={14} />
      </div>

      <LineChartSkeleton className="dash-chart__plot" height={224} />
    </div>
  );
}
